/**
 * Centralized search utilities for Firestore trigram-based search.
 *
 * Firestore does not support native full-text or substring search.
 * We store pre-computed trigrams (3-char sliding windows) in each document's
 * `search_terms` array and query with `array-contains` for server-side filtering.
 *
 * One `array-contains` clause is allowed per Firestore query, so we pick one
 * trigram server-side and refine results client-side.
 */

/**
 * Generates trigrams (3-character sliding windows) from a single text value.
 * Non-alphanumeric characters (except German umlauts) are stripped before slicing.
 */
export function generateTrigrams(text) {
  if (!text || text.length < 3) return [];
  const normalized = text.toLowerCase().replace(/[^a-z0-9äöüß]/gi, '');
  const trigrams = [];
  for (let i = 0; i <= normalized.length - 3; i++) {
    trigrams.push(normalized.substring(i, i + 3));
  }
  return trigrams;
}

/**
 * Builds a deduplicated `search_terms` array for a contact document.
 *
 * Extracts searchable text from all relevant fields, generates trigrams,
 * and returns a flat array capped at 500 entries (well within Firestore's
 * ~1500 index-entry-per-document limit).
 *
 * @param {Object} data - Contact document data
 * @returns {string[]} Trigram array suitable for Firestore `array-contains` queries
 */
export function buildSearchTerms(data) {
  let dmNames = [];
  try {
    const dms = data.lead_decision_makers
      ? (typeof data.lead_decision_makers === 'string'
        ? JSON.parse(data.lead_decision_makers)
        : data.lead_decision_makers)
      : [];
    dmNames = dms.map(dm => dm.name).filter(Boolean);
  } catch (e) { /* malformed DM data — skip */ }

  const igHandle = (data.lead_instagram || data.lead_instagram_username || '')
    .replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/^@/, '').replace(/\/.*$/, '');

  const searchableFields = [
    data.business_name,
    ...dmNames,
    data.lead_firstname,
    data.lead_lastname,
    data.lead_city,
    data.lead_email?.split('@')[0],
    igHandle || null,
  ].filter(Boolean);

  const termSet = new Set();
  searchableFields.forEach(field => {
    generateTrigrams(field).forEach(tri => termSet.add(tri));
  });

  return Array.from(termSet).slice(0, 500);
}
